import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PasswordModal from './PasswordModal';
import { getRestaurantId } from '../config/pocketbase';
import { isSessionAuthorizedForRoute } from '../config/auth';
import { useRestaurantBrand } from '../hooks/useRestaurantBrand';

const { FiMenu, FiUser, FiSettings, FiBarChart3, FiX, FiDollarSign } = FiIcons;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [targetRoute, setTargetRoute] = useState('');
  
  const brand = useRestaurantBrand();
  const currentRestaurant = getRestaurantId();
  const restaurantParam = currentRestaurant ? `?r=${encodeURIComponent(currentRestaurant)}` : '';

  const handleNavClick = (route) => {
    const isAuthorized = isSessionAuthorizedForRoute(route);
    const targetWithParams = `${route}${restaurantParam}`;

    if (!isAuthorized) {
      setTargetRoute(targetWithParams);
      setShowPasswordModal(true);
    } else {
      navigate(targetWithParams);
    }
    setIsOpen(false);
  };

  const navItems = [
    { path: '/menu', label: 'Menù', icon: FiMenu, protected: false },
    { path: '/cameriere', label: 'Cameriere', icon: FiUser, protected: true },
    { path: '/cucina', label: 'Cucina', icon: null, imgSrc: '/icons/cucina-icon.svg', protected: true },
    { path: '/cassa', label: 'Cassa', icon: FiDollarSign, protected: true },
    { path: '/backoffice', label: 'Backoffice', icon: FiSettings, protected: true },
    { path: '/statistiche', label: 'Statistiche', icon: FiBarChart3, protected: true },
  ];

  return (
    <>
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo e Nome Ristorante Dinamico */}
            <Link to={`/${restaurantParam}`} className="flex items-center space-x-3">
              <div 
                className="h-10 w-10 rounded-xl flex items-center justify-center shadow-sm overflow-hidden text-white font-black text-sm"
                style={{ backgroundColor: brand.colore_primario || '#5C715E' }}
              >
                {brand.nome.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'T2'}
              </div>
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-black text-gray-900 leading-tight">
                  {brand.nome}
                </span>
                <span className="text-[11px] font-semibold text-gray-400 tracking-wider">
                  {brand.sottotitolo || 'Menù Digitale & Ordini'}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`flex items-center space-x-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-[#5C715E] text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {item.imgSrc ? (
                      <img src={item.imgSrc} alt={item.label} className="w-4 h-4" />
                    ) : (
                      <SafeIcon icon={item.icon} className="w-4 h-4" />
                    )}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Menu"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Navigation Dropdown */}
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-200 py-3 space-y-1"
            >
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-[#5C715E] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.imgSrc ? (
                      <img src={item.imgSrc} alt={item.label} className="w-5 h-5" />
                    ) : (
                      <SafeIcon icon={item.icon} className="w-5 h-5" />
                    )}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </div>
      </nav>

      {/* Password Modal per accessi non ancora autenticati */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        targetRoute={targetRoute}
      />
    </>
  );
};

export default Navbar;